import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-semibold ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary-dark shadow-lg hover:shadow-xl transform hover:scale-105 hover:glow-primary",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-lg hover:shadow-xl transform hover:scale-105",
        outline:
          "border-2 border-primary bg-transparent text-primary hover:bg-primary hover:text-primary-foreground shadow-md hover:shadow-lg transform hover:scale-105",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-md hover:shadow-lg transform hover:scale-105 hover:glow-secondary",
        ghost: "hover:bg-muted/80 hover:text-foreground transition-all duration-200",
        link: "text-primary underline-offset-4 hover:underline hover:text-primary-dark transition-colors",
        hero: "bg-gradient-to-r from-primary via-primary-light to-secondary text-white hover:from-primary-dark hover:via-primary hover:to-secondary/90 shadow-hero font-bold text-lg px-8 py-4 rounded-xl transform hover:scale-110 hover:glow-primary",
        cta: "bg-gradient-to-r from-accent to-destructive text-white hover:from-accent/90 hover:to-destructive/90 shadow-xl hover:shadow-2xl transform hover:scale-105 font-bold glow-primary",
        success: "bg-success text-success-foreground hover:bg-success/90 shadow-lg hover:shadow-xl transform hover:scale-105 hover:glow-secondary",
        premium: "bg-gradient-to-r from-primary to-accent text-white shadow-xl hover:shadow-2xl transform hover:scale-105 font-bold border border-white/20",
        glass: "bg-white/10 backdrop-blur-md border border-white/30 text-white hover:bg-white/20 shadow-lg hover:shadow-xl transform hover:scale-105",
      },
      size: {
        default: "h-11 px-6 py-3",
        sm: "h-9 rounded-md px-4 py-2",
        lg: "h-13 rounded-lg px-8 py-4 text-base",
        xl: "h-16 rounded-xl px-10 py-5 text-lg",
        icon: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
